import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from '../../../context/UserMetadataContext';
import { PageHeader } from '../../shared';
import apiService from '../services/apiService';
import SummaryCards from './suspicious-activity/SummaryCards';
import SuspiciousUsersTable from './suspicious-activity/SuspiciousUsersTable';
import UserDetailModal from './suspicious-activity/UserDetailModal';
import FilterBar from './suspicious-activity/FilterBar';

const SuspiciousActivityDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    classification: null,
    minSuspicionScore: null,
    skip: 0,
    limit: 50
  });

  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();

  const tenantName = userMetadata?.company || 'trustx';

  // Configure API service with token and tenant
  useEffect(() => {
    const configureApi = async () => {
      try {
        const token = await getAccessTokenSilently();
        apiService.setAuthToken(token);
        apiService.setTenant(tenantName);
      } catch (err) {
        console.error('Error configuring API service:', err);
      }
    };

    if (tenantName) {
      configureApi();
    }
  }, [getAccessTokenSilently, tenantName]);

  // Fetch summary data once on mount
  useEffect(() => {
    fetchSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch filtered user data when filters change
  useEffect(() => {
    fetchUsersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getSuspiciousActivitySummary();

      if (response.success) {
        setSummaryData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch summary');
      }

    } catch (err) {
      console.error('Error fetching summary data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersData = async () => {
    try {
      setUsersLoading(true);

      const response = await apiService.getSuspiciousActivityUsers({
        classification: filters.classification,
        minSuspicionScore: filters.minSuspicionScore,
        skip: filters.skip,
        limit: filters.limit
      });

      if (response.success) {
        const users = response.data;
        setUsersData(Array.isArray(users) ? users : []);
      }

    } catch (err) {
      console.error('Error fetching users data:', err);
      // Don't set global error, just log it
    } finally {
      setUsersLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      skip: 0
    }));
  };

  const handlePageChange = (skip) => {
    setFilters(prev => ({ ...prev, skip }));
  };

  const handleUserClick = async (userId) => {
    try {
      console.log('Fetching user detail for userId:', userId);

      const response = await apiService.getSuspiciousActivityUserDetail(userId);
      console.log('User detail response:', response);

      if (response.success) {
        // API returns { insight: {...}, recent_signins: [...], similar_users: [...] }
        // Merge insight data with recent_signins for the modal
        const userData = {
          ...response.data.insight,
          recent_signins: response.data.recent_signins || [],
          similar_users: response.data.similar_users || []
        };

        console.log('Merged user detail data:', userData);
        setSelectedUser(userData);
        setShowDetailModal(true);
      } else {
        console.error('Failed to fetch user detail:', response.error);
      }
    } catch (err) {
      console.error('Error fetching user detail:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading Suspicious Activity Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="subtitle2" fontWeight={700}>
            Error Loading Dashboard
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Suspicious Activity Detection"
        description="AI-powered detection of security threats and anomalous behavior"
        pillarId="trust"
      />

      {/* Summary Cards */}
      <Box sx={{ mb: 2 }}>
        <SummaryCards data={summaryData} onCardClick={handleFilterChange} />
      </Box>

      {/* Filter Bar */}
      <Box sx={{ mb: 2 }}>
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </Box>

      {/* Suspicious Users Table */}
      <SuspiciousUsersTable
        users={usersData}
        loading={usersLoading}
        onUserClick={handleUserClick}
        onPageChange={handlePageChange}
        currentSkip={filters.skip}
        limit={filters.limit}
      />

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          user={selectedUser}
        />
      )}
    </Box>
  );
};

export default SuspiciousActivityDashboard;
